import React from "react";
import IndustryPage from "../components/IndustryPage";
import { QrCode, Boxes, Route, ShieldCheck } from "lucide-react";

export default function SupplyChain({ onRequestDemo }) {
  return (
    <IndustryPage
      onRequestDemo={onRequestDemo}
      pill="SUPPLY-CHAIN MONITORING"
      title="FROM ORIGIN"
      titleAccent="TO SHELF. EVERY HAND-OFF."
      tagline="VISUAL AUDIT AT EVERY NODE."
      description="StrainScan Pro adds a computer-vision layer to your existing lot-tracking, WMS, and TMS stack — automatically photographing, grading, and time-stamping product at every hand-off. Diversion, damage, and shrinkage stop being he-said-she-said. You have the receipts."
      heroImage="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=85"
      heroLabel="CHAIN VERIFIED"
      heroScore="99.2%"
      sideStats={[
        { label: "CHAIN INTEGRITY", value: "VERIFIED", sub: "12 HAND-OFFS" },
        { label: "LOT MATCH", value: "100%", sub: "SEED-TO-SALE" },
        { label: "TRACE ID", value: "SC-2025-4471", sub: "PALLET 118" },
      ]}
      howSteps={[
        { icon: <QrCode size={22} />, title: "TAG & CAPTURE", desc: "Every lot is photographed alongside its label, QR, or RFID tag at pack-out — one immutable visual receipt." },
        { icon: <Route size={22} />, title: "AUDIT EACH NODE", desc: "Automatic re-scan at every hand-off: warehouse in/out, distribution center, retail intake, back-of-store." },
        { icon: <Boxes size={22} />, title: "VISUAL DIFF", desc: "AI compares the current photo to the origin photo — same product? Same lot? Same condition? Flags mismatches instantly." },
        { icon: <ShieldCheck size={22} />, title: "AUDIT-READY LEDGER", desc: "Every scan is written to a tamper-evident audit trail exportable for regulators, insurers, and internal compliance." },
      ]}
      capabilities={[
        { title: "LOT-LEVEL PHOTOGRAPHIC TRACE", desc: "Every batch gets a photographic identity at origin and every subsequent hand-off — no more paper CoAs alone." },
        { title: "ANOMALY DETECTION", desc: "Flags substituted product, tampered packaging, damaged pallets, and diverted lots the moment they're photographed downstream." },
        { title: "REGULATORY EXPORT", desc: "One-click audit exports formatted for FSMA 204, GS1, GDP, and multi-jurisdiction seed-to-sale mandates in 40+ regions." },
        { title: "ERP / WMS BRIDGE", desc: "REST + webhook integrations with SAP, NetSuite, Oracle WMS, Manhattan, and every major TMS in market." },
        { title: "INSURER-GRADE PROOF", desc: "Photographic + cryptographically-timestamped receipts stand up to insurance disputes, chargebacks, and courtroom evidence." },
        { title: "RECALL IN MINUTES", desc: "When a recall drops, StrainScan Pro identifies every downstream photograph of the affected lot in seconds — not days." },
      ]}
      metrics={[
        { label: "CHAIN ACCURACY", value: "99.2%", sub: "MATCH RATE" },
        { label: "HAND-OFFS TRACKED", value: "∞", sub: "PER LOT" },
        { label: "RECALL SPEED", value: "12 min", sub: "vs. INDUSTRY 3 DAYS" },
        { label: "SHRINK CUT", value: "27%", sub: "TARGET" },
      ]}
      useCases={[
        { kpi: "27%", title: "SHRINK REDUCTION", sub: "TARGET", desc: "Photographic chain-of-custody is designed to deter opportunistic shrinkage and speed insurance recovery on legitimate losses." },
        { kpi: "12min", title: "TO ISOLATE A RECALL", desc: "Locate every downstream instance of a recalled lot in twelve minutes — down from three days on paper systems." },
        { kpi: "FSMA 204", title: "COMPLIANT OUT OF BOX", desc: "Ships with FSMA 204, GS1 EPCIS, and multi-state seed-to-sale exports pre-mapped." },
      ]}
      quote={{
        text: "The supply chain runs on faith — until it doesn't. Recalls, diversions, damages all live in the gap between what a document says and what actually happened. We're closing that gap with a photograph at every hand-off. Truth you can hold up in court.",
        author: "Marshall Perkins",
        role: "Founder & CEO, StrainScan Pro",
      }}
    />
  );
}
